import styled from '@emotion/styled'
import {
  ApiAssetType,
  ApiFee,
  ApiFixedMultiplier,
  ApiMutualFundAsset,
  ApiSegregatedPortfolioAsset,
  ApiStrategyTemplate,
  ApiVariableMultiplier
} from 'api/rtk-api'
import { TimePicker } from 'components/core-v2/atoms/time-picker'
import ToastNotification from 'components/core-v2/atoms/toast-notification/toastNotification'
import { useState } from 'react'

import {
  FeesInput,
  FieldSet,
  FormItem,
  GenericValidationMessages,
  Label,
  LinkButton,
  ModalHeader,
  NumberInput,
  RadioInput,
  Section,
  Select,
  SimpleModal,
  Space,
  StrategySelector,
  TextInput,
  Typography,
  ValidationMessage
} from '../../../components/core-v2'
import Icon from '../../../components/core-v2/atoms/Icon'
import PillLabel from '../../../components/core-v2/atoms/pillLabel/PillLabel'
import IconButton from '../../../components/core-v2/molecules/iconButton'
import { StyledSection } from '../../tradebuilder-v2/modules/01-note/new-note/primitives'
import { Errors } from '../../tradebuilder-v2/util'
import { EditAssetsActions } from '../state/edit-asset-slice'

export type AssetDetailsProps = {
  actions: EditAssetsActions
  asset: ApiMutualFundAsset | ApiSegregatedPortfolioAsset
  availableAssetClasses: string[]
  availableCptyIds: string[]
  availableCurrencies: string[]
  availableStrategies: ApiStrategyTemplate[]
  canProceed: boolean
  canUserSave: boolean
  errors: Errors
  isInUse: boolean
  isNew: boolean
  onChangeStrategyPermission: (strategyId: string, val: boolean) => void
  onSaveAsset: () => void
  saveSuccess: boolean
  serverErrors?: string[]
}

const StyledAddNewAsset = styled.section`
  input[type='radio'] {
    margin-right: 8px;
  }
  input:read-only,
  input:disabled,
  select:disabled {
    background: transparent;
    color: var(--White40);
    cursor: default;
  }
`

const StyledFinswitchBlock = styled.section`
  border-top: Solid 1px var(--White90);
  &:last-child {
    border-bottom: Solid 1px var(--White90);
  }
  * {
    align-self: center;
  }
  label {
    font-weight: normal;
  }
  input {
    width: 50%;
    min-width: 240px;
  }
  &:last-child {
    border-bottom: none;
  }
`

const StyledStrategiesParametersGroup = styled.section`
  width: 50%;
  display: inline-block;
`

const availableFeeBases = ['TotalTradeValue', 'RiskyAssetExposure', 'LoanAmount']
const availableFeeFreqs = ['Quarterly', 'Monthly', 'Annually']
const availableFeeTypes = [
  'GapProtectionPremium',
  'LoanFee',
  'DistributionFee',
  'StrategyFee',
  'PlatformFee',
  'PortfolioManagementFee',
  'NoteFee',
  'ExitFee',
  'TransactionFee',
  'Delta1PremiumFee',
  'RiskControlledPremiumFee',
  'HedgingPremiumFee'
]

export const AssetDetailsTab = ({
  actions,
  asset,
  availableAssetClasses,
  availableCptyIds,
  availableCurrencies,
  availableStrategies,
  canProceed,
  canUserSave,
  errors,
  isInUse,
  isNew,
  onChangeStrategyPermission,
  onSaveAsset,
  saveSuccess,
  serverErrors
}: AssetDetailsProps) => {
  const [showSoftDeleteConfirm, setShowSoftDeleteConfirm] = useState(false)

  const availableDaysList = Array(30)
    .fill(0)
    .map((i, idx) => idx)
  const availableVolTriggers = Array(51)
    .fill(0)
    .map((i, idx) => idx / 100 + 0.05)
  const availableVolWindows = Array(24)
    .fill(0)
    .map((i, idx) => idx * 5 + 10)

  type Selected = 'yes' | 'no' | 'unavailable'
  type Strategy = {
    id: string
    name: string
    selected: Selected
  }
  const strategyList = availableStrategies.map(
    x =>
      ({
        id: x.strategyTemplateId,
        name: x.strategyName,
        selected: asset.disallowedStrategies.includes(x.strategyTemplateId)
          ? ('no' as Selected)
          : ('yes' as Selected)
      } as Strategy)
  )

  let fixedMultiplier = null as ApiFixedMultiplier
  let variableMultiplier = null as ApiVariableMultiplier
  let multiplierType: 'Fixed' | 'Variable' | 'None'
  if (asset.multiplier === null) {
    multiplierType = 'None'
  } else {
    switch (asset.multiplier.multiplierType) {
      case 'Fixed':
        multiplierType = 'Fixed'
        fixedMultiplier = asset.multiplier as ApiFixedMultiplier
        break
      case 'Variable':
        multiplierType = 'Variable'
        variableMultiplier = asset.multiplier as ApiVariableMultiplier
    }
  }

  let mutualFundManager
  let mutualFundIdentifiers
  if (asset.type === 'MutualFund') {
    const mutualFund = asset as ApiMutualFundAsset
    mutualFundManager = mutualFund.fundManager
    mutualFundIdentifiers = mutualFund.externalIdentifiers
  }

  const cutoffTime = new Date(0, 0, 0, asset.cutoffTimeHours, asset.cutoffTimeMinutes)

  const errorsList = []
  for (const errorName in errors) {
    errorsList.push(errors[errorName])
  }

  return (
    <StyledAddNewAsset>
      <Space mx={5} my={6}>
        {!isNew && (
          <FormItem>
            <Label>Status</Label>
            <Space isflex alignItems="center">
              {asset.isDeleted ? (
                <PillLabel>Removed</PillLabel>
              ) : (
                <Typography fontSize={13} fontWeight={400} variant="b3">
                  Active
                </Typography>
              )}
              <Space ml={4}>
                {asset.isDeleted ? (
                  <LinkButton disabled={!canUserSave} onClick={() => actions.restoreAsset()}>
                    Restore
                  </LinkButton>
                ) : (
                  <LinkButton
                    disabled={!canUserSave}
                    onClick={() => setShowSoftDeleteConfirm(true)}
                  >
                    Remove
                  </LinkButton>
                )}
              </Space>
            </Space>
          </FormItem>
        )}
        {showSoftDeleteConfirm && (
          <SimpleModal>
            <ModalHeader onClickClose={() => setShowSoftDeleteConfirm(false)}>
              Remove fund?
            </ModalHeader>
            <Space p={6}>
              <Space mb={6}>
                <Typography fontSize={13} variant="b3">
                  {`Removing "${
                    asset.name
                  }" stops its missing market data and distribution alerts. It stays in valuations and reports and can be restored.${
                    isInUse ? ' Warning: this fund still appears to be in use.' : ''
                  }`}
                </Typography>
              </Space>
              <Space isinlineblock mr={2}>
                <IconButton
                  disabled={false}
                  icon={<Icon name="bin" size="medium" />}
                  iconPosition="left"
                  operationType="negative"
                  text="Remove"
                  onClick={() => {
                    actions.softDeleteAsset()
                    setShowSoftDeleteConfirm(false)
                  }}
                />
              </Space>
              <Space isinlineblock ml={2}>
                <IconButton
                  disabled={false}
                  icon={<Icon name="circleX" size="medium" />}
                  iconPosition="left"
                  operationType="cancel"
                  text="Cancel"
                  onClick={() => setShowSoftDeleteConfirm(false)}
                />
              </Space>
            </Space>
          </SimpleModal>
        )}
        <FormItem>
          <Label>Name *</Label>
          <TextInput value={asset.name} onChange={v => actions.setAssetName(v.target.value)} />
          <ValidationMessage errors={errors} fieldName="asset_name" />
        </FormItem>
        <FormItem>
          <Label>ISIN *</Label>
          <TextInput
            readOnly={isInUse || !isNew}
            value={(asset as any).isinCode}
            onChange={v => actions.setIsin(v.target.value)}
          />
          <ValidationMessage errors={errors} fieldName="isin" />
        </FormItem>
        <FormItem>
          <Label>Reference asset classification *</Label>
          <StyledSection direction="horizontal">
            <FieldSet direction="horizontal">
              <RadioInput
                checked={asset.protectionClassification === 'Performance'}
                disabled={isInUse}
                id="performance"
                type="radio"
                onChange={() => actions.setProtectionClassification('Performance')}
              />
              <label htmlFor="performance">Performance</label>
            </FieldSet>
            <FieldSet direction="horizontal">
              <RadioInput
                checked={asset.protectionClassification === 'Protection'}
                disabled={isInUse}
                id="protection"
                type="radio"
                onChange={() => actions.setProtectionClassification('Protection')}
              />
              <label htmlFor="protection">Protection</label>
            </FieldSet>
          </StyledSection>
        </FormItem>
        <StyledStrategiesParametersGroup>
          <FormItem pr={3}>
            <Label>Type *</Label>
            <Select
              disabled={isInUse}
              value={asset.type}
              onChange={v => actions.setAssetType(v.target.value as ApiAssetType)}
            >
              <option value="MutualFund">Mutual fund</option>
              <option value="SegregatedPortfolio">Segregated portfolio</option>
            </Select>
          </FormItem>
        </StyledStrategiesParametersGroup>
        {asset.type === 'MutualFund' && (
          <div data-private>
            <FormItem>
              <Label>Fund manager name *</Label>
              <TextInput
                readOnly={isInUse}
                value={mutualFundManager}
                onChange={v => actions.setFundManager(v.target.value)}
              />
              <ValidationMessage errors={errors} fieldName="fund_manager" />
            </FormItem>
            <FormItem>
              <Label>Fund manager counterparty ID *</Label>
              <TextInput
                readOnly={isInUse}
                value={
                  mutualFundIdentifiers === undefined
                    ? ''
                    : mutualFundIdentifiers.fundManagerCounterpartyId
                }
                onChange={v => actions.setFundMgrCptyId(v.target.value)}
              />
              <ValidationMessage errors={errors} fieldName="fund_mgr_cpty" />
            </FormItem>
          </div>
        )}
        {asset.type === 'MutualFund' && (
          <FormItem>
            <Label>Finswitch codes</Label>
            <StyledFinswitchBlock>
              <Space isflex justifyContent="space-between" px={5} py={3}>
                <Label>ManCo code</Label>
                <TextInput
                  readOnly={isInUse}
                  value={
                    mutualFundIdentifiers === undefined
                      ? ''
                      : mutualFundIdentifiers.managementCompanyCode
                  }
                  onChange={v => actions.setManagementCoCode(v.target.value)}
                />
              </Space>
              <ValidationMessage errors={errors} fieldName="manco_code" />
            </StyledFinswitchBlock>
            <StyledFinswitchBlock>
              <Space isflex justifyContent="space-between" px={5} py={3}>
                <Label>Fund code</Label>
                <ValidationMessage errors={errors} fieldName="fund_code" />
                <TextInput
                  readOnly={isInUse}
                  value={mutualFundIdentifiers === undefined ? '' : mutualFundIdentifiers.fundCode}
                  onChange={v => actions.setFundCode(v.target.value)}
                />
              </Space>
            </StyledFinswitchBlock>
            <StyledFinswitchBlock>
              <Space isflex justifyContent="space-between" px={5} py={3}>
                <Label>Product code</Label>
                <ValidationMessage errors={errors} fieldName="product_code" />
                <TextInput
                  readOnly={isInUse}
                  value={
                    mutualFundIdentifiers === undefined ? '' : mutualFundIdentifiers.productCode
                  }
                  onChange={v => actions.setProductCode(v.target.value)}
                />
              </Space>
            </StyledFinswitchBlock>
            <StyledFinswitchBlock>
              <Space isflex justifyContent="space-between" px={5} py={3}>
                <Label>Account number</Label>
                <ValidationMessage errors={errors} fieldName="account_number" />
                <TextInput
                  readOnly={isInUse}
                  value={
                    mutualFundIdentifiers === undefined ? '' : mutualFundIdentifiers.accountNumber
                  }
                  onChange={v => actions.setAccountNumber(v.target.value)}
                />
              </Space>
            </StyledFinswitchBlock>
          </FormItem>
        )}
        <FormItem>
          <Label>Trading frequency *</Label>
          <StyledSection direction="horizontal">
            <FieldSet direction="horizontal">
              <RadioInput
                checked={asset.dealingFrequency === 'Daily'}
                id="daily"
                type="radio"
                onChange={() => actions.setDealingFrequency('Daily')}
              />
              <label htmlFor="daily">Daily</label>
            </FieldSet>
            <FieldSet direction="horizontal">
              <RadioInput
                checked={asset.dealingFrequency === 'Monthly'}
                id="monthly"
                type="radio"
                onChange={() => actions.setDealingFrequency('Monthly')}
              />
              <label htmlFor="monthly">Monthly</label>
            </FieldSet>
          </StyledSection>
        </FormItem>
        <StyledStrategiesParametersGroup>
          <FormItem pr={3}>
            <Label>Notice period *</Label>
            <Select
              value={asset.noticePeriodDays}
              onChange={v => actions.setNoticePeriodDays(Number(v.target.value))}
            >
              {availableDaysList.map(c => (
                <option key={c} value={c}>
                  {`${c} Days`}
                </option>
              ))}
            </Select>
          </FormItem>
        </StyledStrategiesParametersGroup>
        <StyledStrategiesParametersGroup>
          <FormItem>
            <Label>Cut off time *</Label>
            <TimePicker
              disabled={false}
              value={cutoffTime}
              onChange={date => {
                const hours = date.getHours()
                const minutes = date.getMinutes()
                actions.setCutOffTime({ hours, minutes })
              }}
            />
          </FormItem>
        </StyledStrategiesParametersGroup>
        <StyledStrategiesParametersGroup>
          <FormItem pr={3}>
            <Label>Subscription settlement lag *</Label>
            <Select
              value={asset.dealingCalendar.dealingPeriodExpectation.settlementSubscription}
              onChange={v => actions.setSubSettlementLag(Number(v.target.value))}
            >
              {availableDaysList.map(c => (
                <option key={c} value={c}>
                  {`${c} Days`}
                </option>
              ))}
            </Select>
          </FormItem>
        </StyledStrategiesParametersGroup>
        <StyledStrategiesParametersGroup>
          <FormItem pr={3}>
            <Label>Redemption settlement lag *</Label>
            <Select
              value={asset.dealingCalendar.dealingPeriodExpectation.settlementRedemption}
              onChange={v => actions.setRedSettlementLag(Number(v.target.value))}
            >
              {availableDaysList.map(c => (
                <option key={c} value={c}>
                  {`${c} Days`}
                </option>
              ))}
            </Select>
          </FormItem>
        </StyledStrategiesParametersGroup>
        <FormItem>
          <Label>Asset class *</Label>
          <Select value={asset.assetClass} onChange={v => actions.setAssetClass(v.target.value)}>
            <option value="">Choose asset class</option>
            {availableAssetClasses.map(c => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
          <ValidationMessage errors={errors} fieldName="asset_class" />
        </FormItem>
        {asset.protectionClassification === 'Performance' && (
          <FormItem>
            <Label>Tax classification *</Label>
            <StyledSection direction="horizontal">
              <FieldSet direction="horizontal">
                <RadioInput
                  checked={asset.taxClassification === 'Capital'}
                  id="capital"
                  type="radio"
                  onChange={() => actions.setTaxClassification('Capital')}
                />
                <label htmlFor="capital">Capital</label>
              </FieldSet>
              <FieldSet direction="horizontal">
                <RadioInput
                  checked={asset.taxClassification === 'Yield'}
                  id="yield"
                  type="radio"
                  onChange={() => actions.setTaxClassification('Yield')}
                />
                <label htmlFor="yield">Yield</label>
              </FieldSet>
            </StyledSection>
          </FormItem>
        )}
        <StyledStrategiesParametersGroup>
          <FormItem pr={3}>
            <Label>Currency *</Label>
            <Select
              disabled={isInUse}
              value={asset.currencyIso}
              onChange={v => actions.setCurrency(v.target.value)}
            >
              {availableCurrencies.map(c => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </FormItem>
        </StyledStrategiesParametersGroup>
        <StyledStrategiesParametersGroup>
          <FormItem pl={3}>
            <Label>Scaling factor *</Label>
            <Select
              disabled={isInUse}
              value={asset.priceScalingFactor}
              onChange={v => actions.setPriceScalingFactor(Number(v.target.value))}
            >
              <option value={1}>1</option>
              <option value={100}>100</option>
            </Select>
          </FormItem>
        </StyledStrategiesParametersGroup>

        <Section mb={5} type="tertiary">
          <FormItem>
            <Section px={5} py={6} type="quaternary">
              <Space mb={3}>
                <Typography fontSize={13} fontWeight={800} variant="b3">
                  Strategies
                </Typography>
              </Space>
              <Typography fontSize={13} fontWeight={400} variant="b3">
                Choose the strategies that this asset can be used with
              </Typography>
            </Section>
            <Section m={0} pb={1} pt={6} px={4} type="nesting-only">
              <StrategySelector strategies={strategyList} onChange={onChangeStrategyPermission} />
            </Section>
          </FormItem>
        </Section>

        {false && (
          <Section mb={5} type="tertiary">
            <FormItem>
              <Section px={5} py={6} type="quaternary">
                <Space mb={3}>
                  <Typography fontSize={13} fontWeight={800} variant="b3">
                    Fees
                  </Typography>
                </Space>
                <Typography fontSize={13} fontWeight={400} variant="b3">
                  Choose the fees that will apply to this asset
                </Typography>
              </Section>
              <Section pt={6} px={5} type="nesting-only">
                <FeesInput
                  availableCptyIds={availableCptyIds}
                  availableFeeBases={availableFeeBases}
                  availableFeeFreqs={availableFeeFreqs}
                  availableFeeTypes={availableFeeTypes}
                  displayFees={asset.fees}
                  feeCategory="portfolio"
                  title="Asset charges"
                  onAddNewFee={(newFee: ApiFee) => actions.addNewFee(newFee)}
                  onFeePropertyChange={(index, property, updatedValue) =>
                    actions.changeFeeProperty({ index, property, updatedValue })
                  }
                  onRemoveExistingFee={(index: number) => actions.removeFee(index)}
                />
              </Section>
              <ValidationMessage errors={errors} fieldName="feeRates" />
            </FormItem>
          </Section>
        )}

        {asset.protectionClassification === 'Performance' && (
          <Section mb={5} type="tertiary">
            <Section px={5} py={6} type="quaternary">
              <Typography fontSize={13} fontWeight={800} variant="b3">
                Strategy parameters
              </Typography>
            </Section>
            <Section pt={6} px={5} type="nesting-only">
              <StyledStrategiesParametersGroup>
                <FormItem pr={3}>
                  <Label>Max leverage ratio *</Label>
                  <NumberInput
                    value={asset.maxLeverageRatio === 0 ? '' : asset.maxLeverageRatio}
                    onChange={v => actions.setMaxLeverageRatio(Number(v.target.value))}
                  />
                  <ValidationMessage errors={errors} fieldName="max_lev_ratio" />
                </FormItem>
              </StyledStrategiesParametersGroup>
              <br />
              <StyledStrategiesParametersGroup>
                <FormItem pr={3}>
                  <Label>Short Lambda *</Label>
                  <NumberInput
                    value={
                      !asset.multiplier ||
                      asset.multiplier.shortLambda === 0 ||
                      asset.multiplier.shortLambda === null
                        ? ''
                        : asset.multiplier.shortLambda
                    }
                    onChange={v => actions.setShortLambda(Number(v.target.value))}
                  />
                  <ValidationMessage errors={errors} fieldName="short_lambda" />
                </FormItem>
              </StyledStrategiesParametersGroup>
              <StyledStrategiesParametersGroup>
                <FormItem pl={3}>
                  <Label>Long Lambda *</Label>
                  <NumberInput
                    value={
                      !asset.multiplier ||
                      asset.multiplier.longLambda === 0 ||
                      asset.multiplier.longLambda === null
                        ? ''
                        : asset.multiplier.longLambda
                    }
                    onChange={v => actions.setLongLambda(Number(v.target.value))}
                  />
                  <ValidationMessage errors={errors} fieldName="long_lambda" />
                </FormItem>
              </StyledStrategiesParametersGroup>
              <StyledStrategiesParametersGroup>
                <FormItem mb={4} pr={3}>
                  <Label>Multiplier *</Label>
                  <StyledSection direction="horizontal">
                    <FieldSet direction="horizontal">
                      <RadioInput
                        checked={
                          !asset.multiplier ? true : asset.multiplier.multiplierType === 'Fixed'
                        }
                        id="fixed"
                        type="radio"
                        onChange={() => actions.setMultiplierType('Fixed')}
                      />
                      <label htmlFor="fixed">Fixed</label>
                    </FieldSet>
                    <FieldSet direction="horizontal">
                      <RadioInput
                        checked={
                          !asset.multiplier ? false : asset.multiplier.multiplierType === 'Variable'
                        }
                        id="variable"
                        type="radio"
                        onChange={() => actions.setMultiplierType('Variable')}
                      />
                      <label htmlFor="variable">Variable</label>
                    </FieldSet>
                  </StyledSection>
                </FormItem>
              </StyledStrategiesParametersGroup>
              <br />
              {multiplierType === 'Fixed' && (
                <StyledStrategiesParametersGroup>
                  <FormItem mb={6} pr={3}>
                    <Label>Fixed multiplier *</Label>
                    <NumberInput
                      value={
                        !fixedMultiplier.multiplier ||
                        fixedMultiplier.multiplier === 0 ||
                        fixedMultiplier.multiplier === null
                          ? ''
                          : fixedMultiplier.multiplier
                      }
                      onChange={v => actions.setFixedMultiplier(Number(v.target.value))}
                    />
                    <ValidationMessage errors={errors} fieldName="fixed_multiplier" />
                  </FormItem>
                </StyledStrategiesParametersGroup>
              )}
              {multiplierType === 'Variable' && (
                <>
                  <StyledStrategiesParametersGroup>
                    <FormItem pl={5} pr={3}>
                      <Label>Standard multiplier *</Label>
                      <NumberInput
                        value={
                          !variableMultiplier.standardMultiplier ||
                          variableMultiplier.standardMultiplier === 0
                            ? ''
                            : variableMultiplier.standardMultiplier
                        }
                        onChange={v => actions.setStandardMultiplier(Number(v.target.value))}
                      />
                      <ValidationMessage errors={errors} fieldName="standard_multiplier" />
                      <ValidationMessage errors={errors} fieldName="reduced_vs_std_multiplier" />
                    </FormItem>
                  </StyledStrategiesParametersGroup>
                  <StyledStrategiesParametersGroup>
                    <FormItem pl={3} pr={5}>
                      <Label>Reduced multiplier *</Label>
                      <NumberInput
                        value={
                          !variableMultiplier.reducedMultiplier ||
                          variableMultiplier.reducedMultiplier === 0
                            ? ''
                            : variableMultiplier.reducedMultiplier
                        }
                        onChange={v => actions.setReducedMultiplier(Number(v.target.value))}
                      />
                      <ValidationMessage errors={errors} fieldName="reduced_multiplier" />
                    </FormItem>
                  </StyledStrategiesParametersGroup>
                  <StyledStrategiesParametersGroup>
                    <FormItem pl={5} pr={3}>
                      <Label>Volatility trigger level</Label>
                      <Select
                        value={variableMultiplier.volatilityTrigger}
                        onChange={v => actions.setVolTriggerLevel(Number(v.target.value))}
                      >
                        {availableVolTriggers.map(c => (
                          <option key={c} value={c}>
                            {`${(c * 100).toFixed(2)}%`}
                          </option>
                        ))}
                      </Select>
                    </FormItem>
                  </StyledStrategiesParametersGroup>
                  <br />
                  <StyledStrategiesParametersGroup>
                    <FormItem mb={6} pl={5} pr={3}>
                      <Label>Short term vol window</Label>
                      <Select
                        value={variableMultiplier.shortTermVolatilityWindow}
                        onChange={v => actions.setShortTermVolWindow(Number(v.target.value))}
                      >
                        {availableVolWindows.map(c => (
                          <option key={c} value={c}>
                            {`${c} Days`}
                          </option>
                        ))}
                      </Select>
                    </FormItem>
                  </StyledStrategiesParametersGroup>
                  <StyledStrategiesParametersGroup>
                    <FormItem mb={6} pl={3} pr={5}>
                      <Label>Long term vol window</Label>
                      <Select
                        value={variableMultiplier.longTermVolatilityWindow}
                        onChange={v => actions.setLongTermVolWindow(Number(v.target.value))}
                      >
                        {availableVolWindows.map(c => (
                          <option key={c} value={c}>
                            {`${c} Days`}
                          </option>
                        ))}
                      </Select>
                    </FormItem>
                  </StyledStrategiesParametersGroup>
                </>
              )}
            </Section>
          </Section>
        )}
        <GenericValidationMessages errorMessages={serverErrors} />
        <GenericValidationMessages errorMessages={errorsList} />
        <Space isflex alignItems="center">
          <IconButton
            disabled={!canProceed}
            icon={<Icon name="save" size="default" />}
            iconPosition="left"
            operationType="progress"
            text="Save"
            onClick={onSaveAsset}
          />
          <ToastNotification
            description={`${asset.name} saved successfully!`}
            notificationType="success"
            open={saveSuccess}
            title="Asset saved"
            onClose={() => actions.setSaveSuccessFalse()}
          />
        </Space>
      </Space>
    </StyledAddNewAsset>
  )
}
